/**
 * Sohbet — gerçek backend (Postgres + REST). 3sn aralık polling ile yeni
 * mesajlar otomatik gelir, send POST ile sunucuya yazılır, thread listesi
 * sunucudan canlı çekilir, "Yeni mesaj" akışında peer email ile
 * `/chat/threads` POST yaparak thread oluşturulur.
 */
import { applyStyle, escapeHtml } from '../ui/css';
import { createButton } from '../ui/components/button';
import { createPageHeader } from '../ui/components/pageHeader';
import { icons } from '../ui/icons';
import { appState } from '../state';
const POLL_INTERVAL_MS = 3000;
export function renderChat() {
    const root = document.createElement('div');
    let activeThreadId = null;
    let searchTerm = '';
    let threads = [];
    let msgs = [];
    let lastSeenAt = null;
    let pollTimer = null;
    // ── DOM iskeleti ──
    const newMsgBtn = createButton({
        label: 'Yeni mesaj',
        variant: 'primary',
        iconHtml: icons.message(),
        onClick: async () => {
            const peer = prompt('Yeni sohbet için kişinin e-postası:');
            if (!peer)
                return;
            try {
                const r = await appState.backend.chatCreateThread(peer);
                await refreshThreads();
                activeThreadId = r.id;
                renderThreads();
                await loadMessagesFor(activeThreadId);
                renderWindow();
            }
            catch (e) {
                alert(`Sohbet oluşturulamadı: ${e}`);
            }
        },
    });
    root.append(createPageHeader({
        title: 'Takım',
        highlight: 'sohbeti',
        subtitle: 'Uçtan uca şifreli mesajlaşma · backend Postgres ile kalıcı',
        rightSlot: newMsgBtn,
    }));
    const stage = document.createElement('div');
    stage.classList.add('pr-chat-shell');
    applyStyle(stage, {
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        gap: 'var(--sp-3)',
        minHeight: '560px',
    });
    // Sol: thread listesi
    const list = document.createElement('aside');
    applyStyle(list, {
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '0',
    });
    const search = document.createElement('div');
    applyStyle(search, { padding: 'var(--sp-3)', borderBottom: '1px solid var(--border-subtle)' });
    search.innerHTML = `
    <div style="position:relative;display:flex;align-items:center">
      <span style="position:absolute;left:10px;color:var(--text-tertiary);display:flex">${icons.search()}</span>
      <input id="thread-search" type="text" placeholder="Sohbet ara…"
             style="width:100%;height:32px;padding:0 var(--sp-3) 0 36px;font-size:var(--fs-caption);background:var(--bg-elev-3);border:1px solid var(--border-subtle);border-radius:var(--radius-sm);color:var(--text-primary)" />
    </div>
  `;
    const searchInput = search.querySelector('#thread-search');
    searchInput.addEventListener('input', () => {
        searchTerm = searchInput.value.toLowerCase().trim();
        renderThreads();
    });
    list.append(search);
    const threadsContainer = document.createElement('div');
    applyStyle(threadsContainer, { flex: '1', overflowY: 'auto' });
    list.append(threadsContainer);
    stage.append(list);
    // Sağ: chat window
    const win = document.createElement('section');
    applyStyle(win, {
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '0',
    });
    const winHead = document.createElement('header');
    applyStyle(winHead, {
        display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-4)',
        borderBottom: '1px solid var(--border-subtle)',
    });
    win.append(winHead);
    const daySep = document.createElement('div');
    applyStyle(daySep, {
        display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-5)',
        fontSize: 'var(--fs-tiny)', color: 'var(--text-tertiary)', letterSpacing: '0.12em',
    });
    daySep.innerHTML = `
    <span style="flex:1;height:1px;background:var(--border-subtle)"></span>
    <span>SOHBET</span>
    <span style="flex:1;height:1px;background:var(--border-subtle)"></span>
  `;
    win.append(daySep);
    const msgsContainer = document.createElement('div');
    applyStyle(msgsContainer, {
        flex: '1', overflowY: 'auto',
        padding: '0 var(--sp-5) var(--sp-4)',
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)',
    });
    win.append(msgsContainer);
    // Input
    const inp = document.createElement('div');
    applyStyle(inp, {
        display: 'flex', gap: 'var(--sp-2)',
        padding: 'var(--sp-3) var(--sp-4)',
        borderTop: '1px solid var(--border-subtle)',
        alignItems: 'center', background: 'var(--bg-elev-3)',
    });
    const inputBox = document.createElement('input');
    inputBox.type = 'text';
    inputBox.placeholder = 'Mesajını yaz…';
    applyStyle(inputBox, {
        flex: '1', height: '36px', padding: '0 var(--sp-3)',
        fontSize: 'var(--fs-body)', background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)',
        color: 'var(--text-primary)',
    });
    const sendBtn = document.createElement('button');
    sendBtn.type = 'button';
    sendBtn.innerHTML = icons.arrowRight();
    applyStyle(sendBtn, {
        width: '36px', height: '36px',
        background: 'var(--accent)', color: 'var(--on-accent)',
        border: '0', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700',
    });
    sendBtn.addEventListener('click', async () => {
        if (!activeThreadId)
            return;
        const v = inputBox.value.trim();
        if (!v)
            return;
        sendBtn.disabled = true;
        try {
            const newMsg = await appState.backend.chatSendMessage(activeThreadId, v);
            msgs.push(newMsg);
            lastSeenAt = newMsg.sentAt;
            // Thread preview / time güncelle
            const t = threads.find(x => x.id === activeThreadId);
            if (t) {
                t.lastMessagePreview = v.length > 60 ? v.slice(0, 60) + '…' : v;
                t.lastMessageAt = newMsg.sentAt;
            }
            msgsContainer.append(buildBubble(newMsg, true));
            msgsContainer.scrollTop = msgsContainer.scrollHeight;
            inputBox.value = '';
            renderThreads();
        }
        catch (e) {
            alert(`Mesaj gönderilemedi: ${e}`);
        }
        finally {
            sendBtn.disabled = false;
            inputBox.focus();
        }
    });
    inputBox.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendBtn.click();
        }
    });
    inp.append(inputBox, sendBtn);
    win.append(inp);
    stage.append(win);
    root.append(stage);
    // Boş state başlangıç banner
    showEmptyWindow();
    // ── Backend besleme ──
    void (async () => {
        await refreshThreads();
        renderThreads();
        if (threads.length > 0) {
            activeThreadId = threads[0].id;
            await loadMessagesFor(activeThreadId);
            renderWindow();
        }
        startPolling();
    })();
    // Cleanup — sayfa unmount olduğunda polling durur
    const observer = new MutationObserver(() => {
        if (!document.body.contains(root)) {
            stopPolling();
            observer.disconnect();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // ── Helpers ──
    async function refreshThreads() {
        try {
            threads = await appState.backend.chatListThreads();
        }
        catch (e) {
            threadsContainer.innerHTML = `<div style="padding:16px;color:var(--status-error);font-size:var(--fs-caption)">Sohbet yüklenemedi: ${escapeHtml(String(e))}</div>`;
        }
    }
    async function loadMessagesFor(tid) {
        try {
            msgs = await appState.backend.chatGetMessages(tid);
            lastSeenAt = msgs.length > 0 ? msgs[msgs.length - 1].sentAt : null;
        }
        catch (e) {
            msgs = [];
            console.error('chat msgs load fail', e);
        }
    }
    function renderThreads() {
        threadsContainer.innerHTML = '';
        const filtered = threads.filter(t => !searchTerm || t.peerEmail.toLowerCase().includes(searchTerm)
            || t.peerDisplay.toLowerCase().includes(searchTerm)
            || (t.lastMessagePreview ?? '').toLowerCase().includes(searchTerm));
        if (filtered.length === 0) {
            const empty = document.createElement('div');
            empty.innerHTML = threads.length === 0
                ? `<div style="padding:24px;text-align:center;color:var(--text-tertiary);font-size:var(--fs-caption)">
            Henüz sohbet yok.<br><br>
            <strong style="color:var(--accent)">Yeni mesaj</strong> ile bir kullanıcının e-postasını girerek başlat.
          </div>`
                : `<div style="padding:16px;text-align:center;color:var(--text-tertiary);font-size:var(--fs-caption)">Sonuç bulunamadı</div>`;
            threadsContainer.append(empty);
            return;
        }
        for (const t of filtered) {
            threadsContainer.append(threadRow(t, t.id === activeThreadId, async () => {
                activeThreadId = t.id;
                renderThreads();
                await loadMessagesFor(t.id);
                renderWindow();
            }));
        }
    }
    function renderWindow() {
        if (!activeThreadId) {
            showEmptyWindow();
            return;
        }
        const t = threads.find(x => x.id === activeThreadId);
        if (!t)
            return;
        winHead.innerHTML = '';
        const headAvatar = document.createElement('div');
        headAvatar.textContent = (t.peerDisplay || '?').slice(0, 2).toUpperCase();
        applyStyle(headAvatar, {
            width: '38px', height: '38px', borderRadius: '50%',
            background: avatarColor(t.peerUserId),
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', fontWeight: '700',
        });
        winHead.append(headAvatar);
        const headInfo = document.createElement('div');
        applyStyle(headInfo, { flex: '1' });
        headInfo.innerHTML = `
      <div style="font-size:var(--fs-body);font-weight:600;color:var(--text-primary)">${escapeHtml(t.peerDisplay || t.peerEmail)}</div>
      <div style="font-size:10px;letter-spacing:0.06em;color:var(--text-tertiary);margin-top:2px">${escapeHtml(t.peerEmail)}</div>
    `;
        winHead.append(headInfo);
        const myUserId = currentUserId();
        msgsContainer.innerHTML = '';
        for (const m of msgs)
            msgsContainer.append(buildBubble(m, m.senderUserId === myUserId));
        msgsContainer.scrollTop = msgsContainer.scrollHeight;
    }
    function showEmptyWindow() {
        winHead.innerHTML = `<div style="font-size:var(--fs-caption);color:var(--text-tertiary);padding:8px 0">Bir sohbet seç ya da yeni başlat.</div>`;
        msgsContainer.innerHTML = '';
    }
    function startPolling() {
        stopPolling();
        pollTimer = window.setInterval(async () => {
            // Yeni mesajlar çek
            if (activeThreadId) {
                try {
                    const newOnes = await appState.backend.chatGetMessages(activeThreadId, lastSeenAt ?? undefined);
                    if (newOnes.length > 0) {
                        const myUserId = currentUserId();
                        for (const m of newOnes) {
                            msgs.push(m);
                            msgsContainer.append(buildBubble(m, m.senderUserId === myUserId));
                        }
                        lastSeenAt = newOnes[newOnes.length - 1].sentAt;
                        msgsContainer.scrollTop = msgsContainer.scrollHeight;
                    }
                }
                catch { /* silent */ }
            }
            // Thread listesi yenile (3 sn'de bir)
            try {
                await refreshThreads();
                renderThreads();
            }
            catch { /* silent */ }
        }, POLL_INTERVAL_MS);
    }
    function stopPolling() {
        if (pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }
    return root;
}
function threadRow(t, isActive, onClick) {
    const row = document.createElement('div');
    applyStyle(row, {
        display: 'flex', gap: 'var(--sp-3)',
        padding: 'var(--sp-3)', cursor: 'pointer',
        borderLeft: '3px solid ' + (isActive ? 'var(--accent)' : 'transparent'),
        background: isActive ? 'var(--bg-elev-3)' : 'transparent',
        transition: 'background var(--motion-fast)',
    });
    row.addEventListener('click', onClick);
    if (!isActive) {
        row.addEventListener('mouseenter', () => row.style.background = 'var(--bg-elev-3)');
        row.addEventListener('mouseleave', () => row.style.background = 'transparent');
    }
    const avatar = document.createElement('div');
    avatar.textContent = (t.peerDisplay || '?').slice(0, 2).toUpperCase();
    applyStyle(avatar, {
        width: '36px', height: '36px', borderRadius: '50%',
        background: avatarColor(t.peerUserId),
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: '700', flexShrink: '0',
    });
    row.append(avatar);
    const info = document.createElement('div');
    applyStyle(info, { flex: '1', minWidth: '0' });
    const top = document.createElement('div');
    applyStyle(top, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' });
    top.innerHTML = `
    <span style="font-size:var(--fs-body);font-weight:500;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0">${escapeHtml(t.peerDisplay || t.peerEmail)}</span>
    <span style="font-size:var(--fs-tiny);color:var(--text-tertiary);font-family:var(--font-mono);flex-shrink:0">${escapeHtml(relTime(t.lastMessageAt ?? t.createdAt))}</span>
  `;
    info.append(top);
    const bottom = document.createElement('div');
    applyStyle(bottom, { marginTop: '2px' });
    bottom.innerHTML = `
    <span style="font-size:var(--fs-caption);color:var(--text-tertiary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:block">${escapeHtml(t.lastMessagePreview ?? '(henüz mesaj yok)')}</span>
  `;
    info.append(bottom);
    row.append(info);
    return row;
}
function buildBubble(m, isOut) {
    const wrap = document.createElement('div');
    applyStyle(wrap, {
        display: 'flex', flexDirection: 'column',
        alignItems: isOut ? 'flex-end' : 'flex-start',
        maxWidth: '70%',
        alignSelf: isOut ? 'flex-end' : 'flex-start',
    });
    const bubble = document.createElement('div');
    applyStyle(bubble, {
        background: isOut ? 'var(--accent)' : 'var(--bg-elev-3)',
        color: isOut ? 'var(--on-accent)' : 'var(--text-primary)',
        padding: '10px 14px',
        borderRadius: isOut ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        fontSize: 'var(--fs-body)', lineHeight: '1.45',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
    });
    bubble.textContent = m.body;
    wrap.append(bubble);
    const t = document.createElement('span');
    t.textContent = formatTime(m.sentAt);
    applyStyle(t, {
        fontSize: 'var(--fs-tiny)', color: 'var(--text-tertiary)',
        marginTop: '4px', fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
    });
    wrap.append(t);
    return wrap;
}
function avatarColor(userId) {
    // userId Guid → deterministic hue
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue} 55% 45%)`;
}
function currentUserId() {
    // accessToken JWT — payload sub claim'inden user ID parse et
    const tok = appState.accessToken;
    if (!tok)
        return '';
    try {
        const parts = tok.split('.');
        if (parts.length < 2)
            return '';
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.sub ?? payload.userId ?? '';
    }
    catch {
        return '';
    }
}
function relTime(iso) {
    if (!iso)
        return '—';
    const t = Date.parse(iso);
    if (Number.isNaN(t))
        return '—';
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1)
        return 'şimdi';
    if (m < 60)
        return `${m} dk`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h} sa`;
    const d = Math.floor(h / 24);
    return `${d} gün`;
}
function formatTime(iso) {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
